import { BookMarked, Box, ChevronDown, FolderPlus, PackagePlus, Plus, Tag, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface PridatMenuProps {
  /** Bez aktívneho výpočtu nie je kam vkladať. */
  maVypocet: boolean;
  /** Kovanie ide do konkrétnej zostavy — bez výberu sa nedá. */
  maVybranuZostavu: boolean;
  /**
   * Kam ide dielec a kovanie — „do zostavy B", „do 3 zostáv" (KOLO 196).
   * Dielec bol do KOLA 196 samostatné tlačidlo vedľa „+ Zostava".
   */
  cielPopis?: string;
  onDielec?: () => void;
  onKovanie: () => void;
  /** Cenovka z hrany (KOLO 197) — dovtedy sa v zákazke nedala pridať. */
  onCenovka?: () => void;
  /** Vlastná položka mimo cenníka (KOLO 210) — LED, oceľ od dodávateľa. */
  onVlastna?: () => void;
  onKomponent: () => void;
  onSablona: () => void;
  /** Nový celok — nadpis nad zostavami (KOLO 235). */
  onCelok?: () => void;
}

/**
 * Vkladanie do kusovníka pod jedným tlačidlom.
 *
 * Hlavné tlačidlo vedľa je „+ Zostava". Dielec je od KOLA 196 prvá
 * položka tu — user: *„hore daj len pridať zostavu, dielec viem pridávať
 * inak"*; ostal v menu so skratkou a cieľom, aby ho našiel aj ten, kto
 * o pravom tlačidle a Ctrl+Enter nevie. Kovanie, Komponent a Šablóna mali
 * kedysi v toolbare rovnakú váhu a Šablóna bola len ikona (KOLO 54) — v menu
 * má každá položka text.
 */
export function PridatMenu({
  maVypocet,
  maVybranuZostavu,
  cielPopis,
  onDielec,
  onKovanie,
  onCenovka,
  onVlastna,
  onKomponent,
  onSablona,
  onCelok,
}: PridatMenuProps): JSX.Element {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={!maVypocet}
          title="Dielec, kovanie, komponent z knižnice alebo uložená šablóna zostavy"
        >
          <Plus className="h-4 w-4" />
          Pridať
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {onDielec ? (
          <DropdownMenuItem
            disabled={!maVybranuZostavu}
            onSelect={onDielec}
            title={
              maVybranuZostavu
                ? `Pridať dielec ${cielPopis ?? "do vybranej zostavy"} (Ctrl+Enter)`
                : "Najprv pridaj zostavu"
            }
          >
            <Plus className="h-4 w-4" />
            <span className="flex-1">
              Dielec
              {cielPopis ? (
                <span className="ml-1 text-xs text-muted-foreground">{cielPopis}</span>
              ) : null}
            </span>
            <span className="pl-3 text-[11px] text-muted-foreground">Ctrl+Enter</span>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          disabled={!maVybranuZostavu}
          onSelect={onKovanie}
          title={
            maVybranuZostavu
              ? "Pridať kovanie do vybranej zostavy"
              : "Najprv klikni na zostavu v kusovníku"
          }
        >
          <Wrench className="h-4 w-4" />
          <span className="flex-1">
            Kovanie
            {cielPopis ? (
              <span className="ml-1 text-xs text-muted-foreground">{cielPopis}</span>
            ) : null}
          </span>
        </DropdownMenuItem>
        {onCenovka ? (
          <DropdownMenuItem
            disabled={!maVybranuZostavu}
            onSelect={onCenovka}
            title={
              maVybranuZostavu
                ? `Pridať cenovku narezanú z hrany ${cielPopis ?? "do vybranej zostavy"}`
                : "Najprv pridaj zostavu"
            }
          >
            <Tag className="h-4 w-4" />
            <span className="flex-1">
              Cenovka (z hrany)
              {cielPopis ? (
                <span className="ml-1 text-xs text-muted-foreground">{cielPopis}</span>
              ) : null}
            </span>
          </DropdownMenuItem>
        ) : null}
        {onVlastna ? (
          <DropdownMenuItem
            disabled={!maVybranuZostavu}
            onSelect={onVlastna}
            title={
              maVybranuZostavu
                ? `LED, oceľ a pod. s cenou od dodávateľa — do cenníka nič nepribudne ${cielPopis ?? ""}`.trim()
                : "Najprv pridaj zostavu"
            }
          >
            <PackagePlus className="h-4 w-4" />
            <span className="flex-1">
              Vlastná položka (mimo cenníka)
              {cielPopis ? (
                <span className="ml-1 text-xs text-muted-foreground">{cielPopis}</span>
              ) : null}
            </span>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onSelect={onKomponent}
          title="Korpus z knižnice/builder, dvere, zásuvka"
        >
          <Box className="h-4 w-4" />
          Komponent
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={onSablona}
          title="Vložiť uloženú šablónu zostavy do výpočtu"
        >
          <BookMarked className="h-4 w-4" />
          Šablóna zostavy
        </DropdownMenuItem>
        {onCelok ? (
          <DropdownMenuItem
            onSelect={onCelok}
            title="Nadpis nad zostavami — Kuchyňa, Obývačka; v ponuke so súčtom"
          >
            <FolderPlus className="h-4 w-4" />
            Celok…
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Pridať ▾ v rozpočte tovaru (KOLO 241, návrh T1) — tovar z cenníka CRC
 * a vlastná položka do vybranej zostavy (inak prvej); regál z konfigurátora
 * má vlastné tlačidlo vedľa (Regál CRC / Upraviť regál).
 */
export function PridatTovarMenu({
  cielPopis,
  onTovar,
  onVlastna,
}: {
  /** „do zostavy B" — bez zostavy `null` a položky sú zamknuté. */
  cielPopis: string | null;
  onTovar: () => void;
  onVlastna: () => void;
}): JSX.Element {
  const dovod = cielPopis ? undefined : "Najprv pridaj zostavu (+ Zostava)";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" title="Tovar z cenníka CRC alebo vlastná položka">
          <Plus className="h-4 w-4" />
          Pridať
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuItem disabled={!cielPopis} onSelect={onTovar} title={dovod}>
          <Tag className="h-4 w-4" />
          Tovar z cenníka{cielPopis ? ` ${cielPopis}` : ""}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!cielPopis} onSelect={onVlastna} title={dovod ?? "Montáž, doprava, položka mimo cenníka"}>
          <PackagePlus className="h-4 w-4" />
          Vlastná položka{cielPopis ? ` ${cielPopis}` : ""}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
